import { isAndroid, isMobile } from 'react-device-detect';

import { isNativeAndroid, isNativeSocialLoginBridgeAvailable, sendToNative } from './NativeUtil';
import type { SocialLoginCredentialInput } from '../data/repositories/SocialLogin';
import type { SocialProvider } from '../data/repositories/SocialLoginMethods';

const GOOGLE_GSI_SCRIPT_ID = 'google-gsi-script';
const GOOGLE_GSI_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
const KAKAO_SDK_SCRIPT_ID = 'kakao-sdk-script';
const KAKAO_SDK_SCRIPT_SRC = 'https://developers.kakao.com/sdk/js/kakao.min.js';
const APPLE_SDK_SCRIPT_ID = 'apple-signin-sdk-script';
const APPLE_SDK_SCRIPT_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
const FACEBOOK_SDK_SCRIPT_ID = 'facebook-jssdk';
const FACEBOOK_SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';

let googleScriptPromise: Promise<void> | null = null;
let kakaoScriptPromise: Promise<void> | null = null;
let appleScriptPromise: Promise<void> | null = null;
let facebookScriptPromise: Promise<void> | null = null;

function loadScript(scriptId: string, src: string): Promise<void> {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve();
  }

  const existingScript = document.getElementById(scriptId) as HTMLScriptElement | null;
  if (existingScript) {
    if (existingScript.dataset.loaded === 'true') {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      existingScript.addEventListener('load', () => resolve(), { once: true });
      existingScript.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), {
        once: true
      });
    });
  }

  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.id = scriptId;
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      script.dataset.loaded = 'true';
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

function loadGoogleScript() {
  if (!googleScriptPromise) {
    googleScriptPromise = loadScript(GOOGLE_GSI_SCRIPT_ID, GOOGLE_GSI_SCRIPT_SRC);
  }
  return googleScriptPromise;
}

function loadKakaoScript() {
  if (!kakaoScriptPromise) {
    kakaoScriptPromise = loadScript(KAKAO_SDK_SCRIPT_ID, KAKAO_SDK_SCRIPT_SRC);
  }
  return kakaoScriptPromise;
}

function loadAppleScript() {
  if (!appleScriptPromise) {
    appleScriptPromise = loadScript(APPLE_SDK_SCRIPT_ID, APPLE_SDK_SCRIPT_SRC);
  }
  return appleScriptPromise;
}

function getMetaAppId() {
  return process.env.REACT_APP_META_APP_ID || '';
}

function loadFacebookScript(): Promise<void> {
  if (facebookScriptPromise) {
    return facebookScriptPromise;
  }

  const appId = getMetaAppId();
  if (!appId) {
    return Promise.reject(new Error('REACT_APP_META_APP_ID is not set'));
  }

  facebookScriptPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      resolve();
      return;
    }

    window.fbAsyncInit = () => {
      window.FB?.init({
        appId,
        cookie: true,
        xfbml: true,
        version: 'v24.0'
      });
      resolve();
    };

    loadScript(FACEBOOK_SDK_SCRIPT_ID, FACEBOOK_SDK_SRC).catch(reject);
  });

  return facebookScriptPromise;
}

function getGoogleClientId() {
  return process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
}

function getKakaoJavaScriptKey() {
  return (
    process.env.REACT_APP_KAKAO_JAVA_SCRIPT_KEY || process.env.REACT_APP_KAKAO_JAVASCRIPT_KEY || ''
  );
}

function getAppleClientId() {
  return process.env.REACT_APP_APPLE_CLIENT_ID || '';
}

function getAppleRedirectUri() {
  const envValue = process.env.REACT_APP_APPLE_REDIRECT_URI?.trim() || '';
  const origin = typeof window !== 'undefined' ? window.location?.origin || '' : '';

  if (envValue) {
    if (/^https?:\/\//i.test(envValue)) {
      return envValue;
    }

    if (!origin) {
      return envValue;
    }

    const normalizedPath = envValue.startsWith('/') ? envValue : `/${envValue}`;
    return new URL(normalizedPath, origin).toString();
  }

  if (origin) {
    return `${origin}/auth/social/callback`;
  }

  return '';
}

function getGoogleRedirectUri() {
  const envValue = process.env.REACT_APP_GOOGLE_REDIRECT_URI?.trim() || '';
  const origin = typeof window !== 'undefined' ? window.location?.origin || '' : '';

  if (envValue) {
    if (/^https?:\/\//i.test(envValue)) {
      return envValue;
    }

    if (!origin) {
      return envValue;
    }

    const normalizedPath = envValue.startsWith('/') ? envValue : `/${envValue}`;
    return new URL(normalizedPath, origin).toString();
  }

  if (origin) {
    return `${origin}/auth/social/callback`;
  }

  return '';
}

const GOOGLE_OAUTH_AUTH_URL = 'https://accounts.google.com/o/oauth2/v2/auth';

const FACEBOOK_OAUTH_DIALOG_VERSION = 'v24.0';

export function buildFacebookOAuthUrl(): string | null {
  const clientId = getMetaAppId();
  const redirectUri = getGoogleRedirectUri();
  if (!clientId || !redirectUri) return null;

  const nonce = createRandomString(24);
  const state = `facebook_${nonce}`;
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'email,public_profile',
    state
  });

  return `https://www.facebook.com/${FACEBOOK_OAUTH_DIALOG_VERSION}/dialog/oauth?${params.toString()}`;
}

export function parseProviderFromState(state: string | null): string {
  if (!state) return 'google';
  const parts = state.split('_');
  return parts[0] === 'google' || parts[0] === 'facebook' ? parts[0] : 'google';
}

export function buildGoogleOAuthUrl(): string | null {
  const clientId = getGoogleClientId();
  const redirectUri = getGoogleRedirectUri();
  if (!clientId || !redirectUri) return null;

  const nonce = createRandomString(24);
  const state = `google_${nonce}`;
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: 'code',
    scope: 'openid email profile',
    state
  });

  return `${GOOGLE_OAUTH_AUTH_URL}?${params.toString()}`;
}

export function requestNativeSocialLogin(provider: 'google' | 'facebook', authUrl: string): void {
  if (!isNativeSocialLoginBridgeAvailable()) {
    window.location.href = authUrl;
    return;
  }

  sendToNative(
    JSON.stringify({
      type: 'nativeSocialLogin',
      provider,
      authUrl
    })
  );
}

function createRandomString(length = 32) {
  const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const randomValues = new Uint8Array(length);
    crypto.getRandomValues(randomValues);

    return Array.from(randomValues)
      .map((value) => alphabet[value % alphabet.length])
      .join('');
  }

  return Array.from({ length })
    .map(() => alphabet[Math.floor(Math.random() * alphabet.length)])
    .join('');
}

function loginWithGoogleAccessTokenPopup(): Promise<SocialLoginCredentialInput | null> {
  const clientId = getGoogleClientId();
  if (!clientId) return Promise.resolve(null);

  return loadGoogleScript()
    .then(() => {
      const googleOauth2 = window.google?.accounts?.oauth2;
      if (!googleOauth2) return null;

      return new Promise<SocialLoginCredentialInput | null>((resolve) => {
        let settled = false;
        const timeoutId = window.setTimeout(() => {
          if (settled) return;
          settled = true;
          resolve(null);
        }, 15000);

        const settle = (value: SocialLoginCredentialInput | null) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeoutId);
          resolve(value);
        };

        const tokenClient = googleOauth2.initTokenClient({
          client_id: clientId,
          scope: 'openid email profile',
          callback: (response) => {
            if (response?.access_token) {
              settle({ accessToken: response.access_token });
              return;
            }

            settle(null);
          },
          error_callback: () => {
            settle(null);
          }
        });

        tokenClient.requestAccessToken({ prompt: 'select_account' });
      });
    })
    .catch(() => null);
}

function loginWithGoogleCredential(): Promise<SocialLoginCredentialInput | null> {
  return loginWithGoogleAccessTokenPopup();
}

function loginWithKakaoAccessToken(): Promise<SocialLoginCredentialInput | null> {
  const appKey = getKakaoJavaScriptKey();
  if (!appKey) return Promise.resolve(null);

  return loadKakaoScript()
    .then(() => {
      if (!window.Kakao) return null;

      if (!window.Kakao.isInitialized()) {
        window.Kakao.init(appKey);
      }

      return new Promise<SocialLoginCredentialInput | null>((resolve) => {
        const preferKakaoTalkApp = isMobile || isNativeAndroid();
        window.Kakao?.Auth.login({
          scope: 'account_email',
          throughTalk: preferKakaoTalkApp,
          success: (authObj) => {
            if (authObj?.access_token) {
              resolve({ accessToken: authObj.access_token });
              return;
            }
            resolve(null);
          },
          fail: () => {
            resolve(null);
          }
        });
      });
    })
    .catch(() => null);
}

function loginWithFacebookAccessToken(): Promise<SocialLoginCredentialInput | null> {
  const appId = getMetaAppId();
  if (!appId) return Promise.resolve(null);

  return loadFacebookScript()
    .then(() => {
      const FB = window.FB;
      if (!FB) return null;

      return new Promise<SocialLoginCredentialInput | null>((resolve) => {
        let settled = false;
        const timeoutId = window.setTimeout(() => {
          if (settled) return;
          settled = true;
          resolve(null);
        }, 15000);

        const settle = (value: SocialLoginCredentialInput | null) => {
          if (settled) return;
          settled = true;
          window.clearTimeout(timeoutId);
          resolve(value);
        };

        FB.login(
          (response: { authResponse?: { accessToken?: string } }) => {
            if (response?.authResponse?.accessToken) {
              settle({ accessToken: response.authResponse.accessToken });
              return;
            }
            settle(null);
          },
          {
            scope: 'email,public_profile'
          }
        );
      });
    })
    .catch(() => null);
}

function loginWithAppleCredential(): Promise<SocialLoginCredentialInput | null> {
  const clientId = getAppleClientId();
  const redirectUri = getAppleRedirectUri();
  if (!clientId || !redirectUri) return Promise.resolve(null);

  return loadAppleScript()
    .then(async () => {
      const appleAuth = window.AppleID?.auth;
      if (!appleAuth) return null;

      try {
        appleAuth.init({
          clientId,
          scope: 'name email',
          redirectURI: redirectUri,
          state: createRandomString(24),
          nonce: createRandomString(24),
          usePopup: !isAndroid,
          responseType: 'code id_token'
        });

        const response = await appleAuth.signIn();
        const authorization = response?.authorization;
        if (!authorization) return null;

        return {
          ...(authorization.id_token ? { idToken: authorization.id_token } : {}),
          ...(authorization.code ? { authorizationCode: authorization.code } : {}),
          redirectUri
        };
      } catch (_error) {
        return null;
      }
    })
    .catch(() => null);
}

const NATIVE_KAKAO_TIMEOUT_MS = 60000;
const NATIVE_KAKAO_TIMEOUT_ANDROID_MS = 25000;

export function getCredentialFromNativeKakao(): Promise<SocialLoginCredentialInput | null> {
  if (!isNativeSocialLoginBridgeAvailable()) {
    return Promise.resolve(null);
  }

  const timeoutMs = isNativeAndroid() ? NATIVE_KAKAO_TIMEOUT_ANDROID_MS : NATIVE_KAKAO_TIMEOUT_MS;

  return new Promise<SocialLoginCredentialInput | null>((resolve) => {
    const timeoutId = window.setTimeout(() => {
      cleanup();
      resolve(null);
    }, timeoutMs);

    const handler = (event: Event) => {
      const customEvent = event as CustomEvent<{
        provider: string;
        status: 'success' | 'cancel' | 'error';
        accessToken?: string;
        errorCode?: string;
        errorMessage?: string;
        timestamp: number;
      }>;
      const result = customEvent.detail;

      if (result.provider !== 'kakao') return;

      cleanup();

      if (result.status === 'success' && result.accessToken) {
        resolve({ accessToken: result.accessToken });
        return;
      }

      resolve(null);
    };

    const cleanup = () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('native:socialLoginResult', handler as EventListener);
    };

    window.addEventListener('native:socialLoginResult', handler as EventListener);
    sendToNative(JSON.stringify({ type: 'nativeSocialLogin', provider: 'kakao' }));
  });
}

export function getKakaoCredentialWithBridgeFallback(): Promise<SocialLoginCredentialInput | null> {
  if (!isNativeSocialLoginBridgeAvailable()) {
    return getSocialCredentialFromWeb('kakao');
  }

  return getCredentialFromNativeKakao().then((cred) => {
    if (cred) return cred;
    if (isNativeAndroid()) {
      return getSocialCredentialFromWeb('kakao');
    }
    return null;
  });
}

export function getSocialCredentialFromWeb(
  provider: SocialProvider
): Promise<SocialLoginCredentialInput | null> {
  if (provider === 'google') {
    return loginWithGoogleCredential();
  }

  if (provider === 'kakao') {
    return loginWithKakaoAccessToken();
  }

  if (provider === 'apple') {
    return loginWithAppleCredential();
  }

  if (provider === 'facebook') {
    return loginWithFacebookAccessToken();
  }

  return Promise.resolve(null);
}
